import { useEffect, useRef } from 'react';

/**
 * L'app è passata in secondo piano, o è tornata davanti.
 *
 * Nel percorso di gioco non c'era **nessun** gestore di visibilità: gli unici
 * due in tutto il repo stanno in `useAppUpdate` (service worker) e in
 * `DashboardPage` (ricarica delle card). L'app quindi non sapeva di essere
 * andata in secondo piano, e da lì nascevano due cose distinte:
 *
 *  - si restava **in coda** mentre non si guardava lo schermo, e
 *    l'accoppiatore — che gira ogni due secondi — assegnava una partita che
 *    l'utente non vede. Il server mette in pausa da sé, ma solo DOPO che il
 *    socket è caduto: fino a 45 secondi di finestra scoperta;
 *  - al ritorno nessuno svegliava il socket, che su iOS può essere morto in
 *    silenzio pur risultando `connected`.
 *
 * ⚠️ Quel che questo hook **non** deve fare è fermare l'orologio di una partita
 * in corso. L'avversario sta aspettando davanti allo schermo, e regalare tempo
 * a chi cambia app sarebbe un exploit banale (basta alternare due app per
 * giocare a tempo illimitato). È la stessa scelta che il server dichiara in
 * `room.ts` — «l'orologio non è mai stato messo in pausa, ha continuato a
 * scorrere durante la disconnessione, come Lichess» — e va difesa.
 *
 * Perché tre eventi e non solo `visibilitychange`:
 *  - `visibilitychange` è quello canonico e copre il cambio di scheda e il
 *    passaggio in secondo piano su Android;
 *  - `pagehide`/`pageshow` coprono iOS, dove il passaggio ad un'altra app può
 *    congelare la pagina senza emettere `visibilitychange`, e dove il ritorno
 *    dalla bfcache emette `pageshow` e basta.
 *
 * Le callback si leggono da una `ref`: chi lo usa passa quasi sempre funzioni
 * ricreate a ogni render, e riagganciare gli ascoltatori ogni volta sarebbe
 * lavoro inutile — oltre a poter perdere un evento nel mezzo.
 */
export function useVisibility(handlers: {
  onHidden?: () => void;
  onVisible?: () => void;
}): void {
  const ref = useRef(handlers);
  ref.current = handlers;

  useEffect(() => {
    const nascosto = (): void => ref.current.onHidden?.();
    const visibile = (): void => ref.current.onVisible?.();

    const suVisibilityChange = (): void => {
      if (document.visibilityState === 'hidden') nascosto();
      else visibile();
    };

    /*
     * `pagehide` scatta anche quando la pagina viene scaricata per davvero, e
     * in quel caso non c'è più niente da mettere in pausa: `persisted` distingue
     * il congelamento (torneremo) dalla chiusura (non torneremo). In entrambi i
     * casi però uscire dalla coda è la cosa giusta, quindi non si filtra.
     */
    const suPageHide = (): void => nascosto();
    const suPageShow = (): void => {
      if (document.visibilityState !== 'hidden') visibile();
    };

    document.addEventListener('visibilitychange', suVisibilityChange);
    window.addEventListener('pagehide', suPageHide);
    window.addEventListener('pageshow', suPageShow);

    return () => {
      document.removeEventListener('visibilitychange', suVisibilityChange);
      window.removeEventListener('pagehide', suPageHide);
      window.removeEventListener('pageshow', suPageShow);
    };
  }, []);
}
